import { Badge, Button, Drawer, Group, Stack, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconBed, IconPlus } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LinkedEntitiesSection } from 'src/Components/EntityLinks/LinkedEntitiesSection';
import { LinkTrail } from 'src/Components/EntityLinks/LinkTrail';
import { useDeleteBed } from 'src/Hooks/Mutations/useCartographyMutations';
import { LinkableEntityType } from 'src/Services/API/EntityLinks.api';
import { BedDto } from 'src/types/cartography.types';

import { BedForm } from '../BedForm';
import { BedItem } from '../BedItem';

import styles from './RoomDetailDrawer.module.scss';
import { Props } from './RoomDetailDrawer.types';

const RoomDetailDrawer = ({ room, opened, openBedFormOnMount, onClose }: Props) => {
  const { t } = useTranslation();
  const [bedFormOpen, setBedFormOpen] = useState(false);
  const [editingBed, setEditingBed] = useState<BedDto | undefined>();

  // Clic sur « + Ajouter un couchage » ou pièce fraîchement créée : on déplie
  // directement le formulaire pour que le flux d'ajout tienne en un geste.
  const roomId = room?.id;
  useEffect(() => {
    if (opened && openBedFormOnMount && roomId) {
      setEditingBed(undefined);
      setBedFormOpen(true);
    }
  }, [opened, openBedFormOnMount, roomId]);

  // Rejoint le mode liste quand on change de pièce : évite d'afficher le formulaire
  // d'une pièce dans le contexte d'une autre après un re-render.
  useEffect(() => {
    if (!openBedFormOnMount) setBedFormOpen(false);
    setEditingBed(undefined);
  }, [roomId, openBedFormOnMount]);

  const { mutate: deleteBed } = useDeleteBed({
    onSuccess: () => {
      notifications.show({
        message: t('admin.structure.beds.deletedNotification', 'Couchage supprimé'),
        color: 'teal',
      });
    },
  });

  const handleEdit = (bed: BedDto) => {
    setEditingBed(bed);
    setBedFormOpen(true);
  };

  const handleDelete = (bed: BedDto) => {
    modals.openConfirmModal({
      title: t('admin.structure.beds.deleteConfirm', 'Supprimer ce couchage ?'),
      children: (
        <Text size="sm">
          {t('admin.structure.beds.deleteConfirmDetail', {
            count: bed.capacity,
            defaultValue: "La capacité d'accueil de la pièce baisse de {{count}}.",
          })}
        </Text>
      ),
      labels: { confirm: t('common.delete', 'Supprimer'), cancel: t('common.cancel', 'Annuler') },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteBed(bed.id),
    });
  };

  const handleBedFormDone = () => {
    setBedFormOpen(false);
    setEditingBed(undefined);
  };

  const handleOpenCreate = () => {
    setEditingBed(undefined);
    setBedFormOpen(true);
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      padding={0}
      title={room?.name ?? ''}
      classNames={{ body: styles.drawerBody, header: styles.drawerHeader }}
    >
      {room && (
        <div className={styles.container}>
          <Stack gap="md" className={styles.scrollArea}>
            <LinkTrail />
            <div className={styles.metaRow}>
              <span>{t(`admin.structure.roomTypes.${room.type}`, room.type)}</span>
              {room.areaSqm != null && <span>· {room.areaSqm} m²</span>}
              {room.isOutOfService && (
                <Badge size="sm" variant="light" color="gray">
                  {t('admin.structure.rooms.outOfService', 'Hors service')}
                </Badge>
              )}
            </div>

            <div className={styles.summary}>
              <Group gap="xs" align="center">
                <IconBed size={20} className={styles.summaryIcon} />
                <span className={styles.summaryLabel}>
                  {t('admin.structure.beds.title', 'Couchages')} :
                </span>
                <span className={styles.summaryValue}>{room.beds.length}</span>
              </Group>
              <Group gap="xs">
                <span className={styles.summaryLabel}>
                  {t('admin.structure.metrics.capacity', "Capacité d'accueil")} :
                </span>
                <span className={styles.summaryCapacity}>
                  {t('admin.structure.beds.capacity', {
                    count: room.capacity,
                    defaultValue: '{{count}} personne',
                    defaultValue_other: '{{count}} personnes',
                  })}
                </span>
              </Group>
            </div>

            {bedFormOpen ? (
              <BedForm roomId={room.id} bed={editingBed} onDone={handleBedFormDone} />
            ) : (
              <>
                <Button
                  variant="primary"
                  leftSection={<IconPlus size={16} />}
                  onClick={handleOpenCreate}
                  fullWidth
                >
                  {t('admin.structure.beds.add', 'Ajouter un couchage')}
                </Button>

                {room.beds.length === 0 ? (
                  <div className={styles.empty}>
                    {t('admin.structure.beds.empty', 'Aucun couchage dans cette pièce')}
                  </div>
                ) : (
                  <div className={styles.bedList}>
                    {room.beds.map(bed => (
                      <BedItem key={bed.id} bed={bed} onEdit={handleEdit} onDelete={handleDelete} />
                    ))}
                  </div>
                )}
              </>
            )}

            {!bedFormOpen && (
              <LinkedEntitiesSection
                entityType={LinkableEntityType.Room}
                entityId={room.id}
                entityTitle={room.name}
              />
            )}
          </Stack>

          {!bedFormOpen && (
            <div className={styles.footer}>
              <Button variant="primary" fullWidth onClick={onClose}>
                {t('admin.structure.beds.done', 'Terminé')}
              </Button>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
};

export default RoomDetailDrawer;
